import { Button, Modal, Radio, RadioGroup } from "@peer-start/ui/reducate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { quizFor, quizScore } from "../data/starting-teacher-quiz.js";

export function StartingTeacherQuizModal({
  skillId,
  open,
  onClose,
  onResult,
}: {
  skillId: string;
  open: boolean;
  onClose: () => void;
  onResult: (result: ReturnType<typeof quizScore>) => void;
}) {
  const { t } = useTranslation();
  const questions = quizFor(skillId);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [score, setScore] = useState<ReturnType<typeof quizScore> | null>(null);
  const question = questions[index];

  useEffect(() => {
    if (!open) return;
    setIndex(0);
    setAnswers({});
    setScore(null);
  }, [open]);

  const finish = (next: Record<string, string>) => {
    const result = quizScore(skillId, next);
    setScore(result);
    onResult(result);
  };

  return (
    <Modal
      closeLabel={t("common.close")}
      controlId="starting-teacher.quiz"
      onClose={onClose}
      open={open}
      title={t("startingTeacher.quiz.title")}
    >
      {score ? (
        <div className="space-y-4">
          <p className="text-lg font-medium" role="status">
            {t("startingTeacher.quiz.score", score)}
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t(score.perfect ? "startingTeacher.quiz.perfect" : "startingTeacher.quiz.short")}
          </p>
          <div className="flex justify-end gap-2">
            {score.perfect ? null : (
              <Button
                controlId="starting-teacher.quiz.retry"
                onClick={() => {
                  setIndex(0);
                  setAnswers({});
                  setScore(null);
                }}
                type="button"
                variant="secondary"
              >
                {t("startingTeacher.quiz.retry")}
              </Button>
            )}
            <Button controlId="starting-teacher.quiz.done" onClick={onClose} type="button">
              {t("startingTeacher.quiz.done")}
            </Button>
          </div>
        </div>
      ) : question ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("startingTeacher.quiz.questionProgress", { current: index + 1, total: questions.length })}
          </p>
          <RadioGroup label={t(`startingTeacher.quiz.questions.${skillId}.${question.id}.prompt`)}>
            {question.options.map((option) => {
              return (
                <div
                  className="rounded-input border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                  key={option}
                >
                  <Radio
                    checked={answers[question.id] === option}
                    data-control-id={`starting-teacher.quiz.option.${option}`}
                    id={`starting-teacher-quiz-${question.id}-${option}`}
                    label={t(`startingTeacher.quiz.questions.${skillId}.${question.id}.${option}`)}
                    name={`starting-teacher-quiz-${question.id}`}
                    onChange={() => setAnswers((current) => ({ ...current, [question.id]: option }))}
                    value={option}
                  />
                </div>
              );
            })}
          </RadioGroup>
          <div className="flex justify-end">
            <Button
              controlId="starting-teacher.quiz.next"
              disabled={!answers[question.id]}
              onClick={() => {
                if (index === questions.length - 1) finish(answers);
                else setIndex((current) => current + 1);
              }}
              type="button"
            >
              {t(
                index === questions.length - 1 ? "startingTeacher.quiz.finish" : "startingTeacher.quiz.next",
              )}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("common.error")}</p>
      )}
    </Modal>
  );
}
